export const PEOPLE_LAYER_SCHEMA_VERSION = 'people_layer_dataset_v1' as const;
export const PEOPLE_LAYER_CERTAINTY_CLASS = 'extrapolated_assumption' as const;

export type PeopleLayerSchemaVersion = typeof PEOPLE_LAYER_SCHEMA_VERSION;
export type PeopleLayerCertaintyClass = typeof PEOPLE_LAYER_CERTAINTY_CLASS;

export type PeopleLayerCapabilityStrength = 'PRIMARY_OWNER' | 'CONTRIBUTOR';
export type PeopleLayerEmploymentType = 'employee' | 'contractor';
export type PeopleLayerSkillProficiency = 'working' | 'advanced' | 'expert';
export type PeopleLayerCurrencyCode = 'USD';

export type PeopleLayerCapabilityRef = {
  capabilityId: string;
  label: string;
  description: string;
  certaintyClass: PeopleLayerCertaintyClass;
};

export type PeopleLayerWorkItem = {
  workItemId: string;
  workstreamId: string;
  title: string;
  strategyObjectiveId: string;
  requiredCapabilityIds: string[];
  demandHoursP50: number;
  certaintyClass: PeopleLayerCertaintyClass;
};

export type PeopleLayerWorkstream = {
  workstreamId: string;
  title: string;
  strategyObjectiveId: string;
  requiredCapabilityIds: string[];
  workItems: PeopleLayerWorkItem[];
  certaintyClass: PeopleLayerCertaintyClass;
};

export type PeopleLayerRoleFamily = {
  roleFamilyId: string;
  title: string;
  level: string;
  functionName: string;
  jobFamily: string;
  requiredCapabilityIds: string[];
  workstreamIds: string[];
  certaintyClass: PeopleLayerCertaintyClass;
};

export type PeopleLayerSeat = {
  seatId: string;
  personId: string | null;
  managerSeatId: string | null;
  title: string;
  level: string;
  functionName: string;
  department: string;
  jobFamily: string;
  location: string;
  roleFamilyId: string;
  employmentType: PeopleLayerEmploymentType;
  fte: number;
  hoursPerWeek: number;
  workstreamIds: string[];
  workItemIds: string[];
  capabilityIds: string[];
  fullyLoadedAnnualCostUsd: number;
  certaintyClass: PeopleLayerCertaintyClass;
};

export type PeopleLayerCapabilityAssignment = {
  assignmentId: string;
  seatId: string;
  positionId: string;
  capabilityId: string;
  strength: PeopleLayerCapabilityStrength;
  workstreamId: string;
  workItemIds: string[];
  evidenceRefId: string;
  certaintyClass: PeopleLayerCertaintyClass;
};

export type PeopleLayerJobDescription = {
  jobDescriptionId: string;
  roleFamilyId: string;
  title: string;
  requiredCapabilityIds: string[];
  workstreamIds: string[];
  responsibilities: string[];
  evidenceRefIds: string[];
  certaintyClass: PeopleLayerCertaintyClass;
};

export type PeopleLayerSkill = {
  skillId: string;
  label: string;
  capabilityId: string;
  proficiency: PeopleLayerSkillProficiency;
  certaintyClass: PeopleLayerCertaintyClass;
};

export type PeopleLayerSkillProfile = {
  skillProfileId: string;
  seatId: string;
  roleFamilyId: string;
  skills: PeopleLayerSkill[];
  evidenceRefIds: string[];
  certaintyClass: PeopleLayerCertaintyClass;
};

export type PeopleLayerReviewSnippet = {
  reviewSnippetId: string;
  seatId: string;
  roleFamilyId: string;
  workItemIds: string[];
  feedbackType: 'manager_review' | 'self_review' | 'peer_360';
  text: string;
  evidenceRefIds: string[];
  certaintyClass: PeopleLayerCertaintyClass;
};

export type PeopleLayerFlcFormula = {
  compensationBand: string;
  compensationBandMidpointUsd: number;
  geoKey: string;
  geoCompMultiplier: number;
  burdenRate: number;
  contractorPremiumRate: number;
  fte: number;
  totalFullyLoadedCostUsd: number;
};

export type PeopleLayerFlcEvidence = {
  flcEvidenceId: string;
  seatId: string;
  positionId: string;
  personId: string | null;
  currency: PeopleLayerCurrencyCode;
  formula: PeopleLayerFlcFormula;
  assumptionRefIds: string[];
  certaintyClass: PeopleLayerCertaintyClass;
};

export type PeopleLayerPortfolioKernelPosition = {
  positionId: string;
  title: string;
  reportsTo: string | null;
  isManager: boolean;
  personId: string | null;
  hoursPerWeek: number;
  fte: number;
  certaintyClass: PeopleLayerCertaintyClass;
};

export type PeopleLayerPortfolioKernelCapabilityAssignment = {
  positionId: string;
  capabilityId: string;
  strength: PeopleLayerCapabilityStrength;
  certaintyClass: PeopleLayerCertaintyClass;
};

export type PeopleLayerPortfolioKernelEvidence = {
  orgSnapshotId: string;
  positions: PeopleLayerPortfolioKernelPosition[];
  capabilityAssignments: PeopleLayerPortfolioKernelCapabilityAssignment[];
  certaintyClass: PeopleLayerCertaintyClass;
};

export type PeopleLayerSampleSeatTrail = {
  seatId: string;
  roleFamilyId: string;
  jobDescriptionId: string;
  workItemIds: string[];
  reviewSnippetId: string;
  skillProfileId: string;
  flcEvidenceId: string;
  certaintyClass: PeopleLayerCertaintyClass;
};

export type PeopleLayerSourceRefs = {
  orgStructure: string;
  strategyPack: string;
  portfolioKernelContract: string;
  orgWorkSignalsLoader: string;
  compBandContract: string;
  flcContract: string;
};

export type PeopleLayerCoherenceModel = {
  chain: string;
  rule: string;
};

export type PeopleLayerRecordFile<T> = {
  schemaVersion: string;
  certaintyClass: PeopleLayerCertaintyClass;
  items: T[];
};

export type PeopleLayerPortfolioKernelEvidenceFile = PeopleLayerPortfolioKernelEvidence & {
  schemaVersion: 'people_layer_portfolio_kernel_evidence_v1';
  certaintyClass: PeopleLayerCertaintyClass;
};

export type PeopleLayerValidationSummary = {
  ok: boolean;
  errors: string[];
  metrics: {
    seatCount: number;
    contractorRatio: number;
    contractorRatioWithinBounds: boolean;
    orphanSkillCount: number;
    flcArithmeticErrorCount: number;
    orphanCapabilityAssignmentCount: number;
    requiredCapabilityMissingSupplyCount: number;
  };
};

export type PeopleLayerDataset = {
  schemaVersion: PeopleLayerSchemaVersion;
  datasetId: string;
  tenantId: string;
  strategyId: string;
  strategyPackVersionId: string;
  generatedAt: string;
  orgSnapshotId: string;
  certaintyClass: PeopleLayerCertaintyClass;
  sourceRefs: PeopleLayerSourceRefs;
  coherenceModel: PeopleLayerCoherenceModel;
  assumptions: Array<{
    assumptionId: string;
    label: string;
    value: string;
    certaintyClass: PeopleLayerCertaintyClass;
  }>;
  capabilities: PeopleLayerCapabilityRef[];
  workstreams: PeopleLayerWorkstream[];
  roleFamilies: PeopleLayerRoleFamily[];
  seats: PeopleLayerSeat[];
  people: PeopleLayerSeat[];
  capabilityAssignments: PeopleLayerCapabilityAssignment[];
  jobDescriptions: PeopleLayerJobDescription[];
  skillProfiles: PeopleLayerSkillProfile[];
  reviewSnippets: PeopleLayerReviewSnippet[];
  flcEvidence: PeopleLayerFlcEvidence[];
  portfolioKernelEvidence: PeopleLayerPortfolioKernelEvidence;
  sampleSeatTrail: PeopleLayerSampleSeatTrail;
};
